import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  Chip,
  Paper,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import FilterStudents from './FilterStudents';
import ModalDialog from '../UIElements/ModalDialog';

import {
  downloadAllStudentsMarksExcel,
  downloadAllStudentsMarksPDF,
  downloadAllStudentsRankWisePDF,
  downloadStudentMarksReport,
  getStudentsByFiltersForReports,
} from '../../features/marks/marksSlice';
import { getExaminationsList } from '../../features/examinations/examinationSlice';
import { getClasses } from '../../features/classes/ClassSlice';

import useNotification from '../../hooks/useNotification';
import Spinner from '../UIElements/Spinner';
import { fetchGrades } from '../../features/grades/gradeSlice';

import { CheckCircle, Cancel, Print } from '@mui/icons-material';
import { RiFileExcel2Line } from 'react-icons/ri';
import { BsFileEarmarkPdf } from 'react-icons/bs';
import { LiaFileDownloadSolid } from 'react-icons/lia';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { MenuProps } from '../../utils/constants';
import { LuListOrdered } from 'react-icons/lu';
import ToastNotification from '../UIElements/ToastNotification';

const MarksReportTab = () => {
  const dispatch = useDispatch();

  const defaultFilters = {
    examType: 'Select',
    department: 'Select',
    section: 'Select',
    class: 'Select',
  };

  const [examTypes, setExamTypes] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentFilters, setCurrentFilters] = useState(defaultFilters);
  const [isLoadingStudents, setIsLoadingStudents] = useState(false);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);
  const [grades, setGrades] = useState([]);
  const [isPrinting, setIsPrinting] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedStudentForView, setSelectedStudentForView] = useState(null);
  const [selectedGrade, setSelectedGrade] = useState('All');
  const [activeFilters, setActiveFilters] = useState(defaultFilters);
  const { notification, hideNotification, showError } = useNotification();

  const { instituteType } = useSelector((state) => {
    return state.auth?.successData?.userDetails ?? {};
  });

  // Filter students based on search and grade filters
  useEffect(() => {
    if (!students?.studentMaks?.length) {
      setFilteredStudents([]);
      return;
    }

    const normalizedSearch = searchTerm?.toString()?.trim()?.toLowerCase();
    const hasSearchTerm = Boolean(normalizedSearch);
    const hasGradeFilter = selectedGrade && selectedGrade !== 'All';

    const filtered = students.studentMaks.filter((student) => {
      // Apply search filter
      if (hasSearchTerm) {
        const matchesName = student?.studentName
          ?.toLowerCase()
          ?.includes(normalizedSearch);
        const matchesId = student?.studentId
          ?.toString()
          ?.toLowerCase()
          ?.includes(normalizedSearch);

        if (!matchesName && !matchesId) return false;
      }

      // Apply grade filter
      if (hasGradeFilter) {
        return student?.grade === selectedGrade;
      }

      return true;
    });

    setFilteredStudents(filtered);
  }, [searchTerm, students?.studentMaks, selectedGrade, grades]);

  const handleSearch = async (searchFilters) => {
    if (isLoadingStudents) return;

    setIsLoadingStudents(true);
    setFilteredStudents([]);
    try {
      const res = await dispatch(
        getStudentsByFiltersForReports({
          examType: searchFilters.examType,
          sectionName: searchFilters.section,
          classesName: searchFilters.class,
          departmentname: searchFilters.department,
          instituteType: searchFilters.instituteType,
        }),
      ).unwrap();
      setActiveFilters(searchFilters);
      setStudents(res || []);
      setFilteredStudents(res?.studentMaks || []);
      const isFindSelectedStudent = res?.studentMaks?.find(
        (s) => s.id === selectedStudent?.id,
      );
      if (!isFindSelectedStudent) {
        setSelectedStudent(null);
      } else {
        setSelectedStudent(isFindSelectedStudent);
      }
    } catch (error) {
      console.error('Error searching students:', error);
      showError('Failed to search students');
    } finally {
      setIsLoadingStudents(false);
    }
  };
  const handleFiltersChange = (newFilters) => {
    setCurrentFilters(newFilters);
  };

  const handleClearFilters = (clearedFilters) => {
    setCurrentFilters(clearedFilters);
    setFilteredStudents([]);
    setStudents([]);
    setSelectedStudent(null);
  };

  useEffect(() => {
    getFilters();
  }, [dispatch]);

  const getFilters = async () => {
    setIsLoadingFilters(true);
    try {
      const examTypesRes = await dispatch(getExaminationsList()).unwrap();
      const departmentRes = await dispatch(getClasses()).unwrap();
      const grades = await dispatch(fetchGrades()).unwrap();

      setGrades(grades || []);
      setExamTypes(examTypesRes || []);
      setDepartments(departmentRes || []);
      // To remove  the default selected  grade
      // if (departmentRes?.length > 0) {
      //   setCurrentFilters((prev) => ({
      //     ...prev,
      //     department: departmentRes[0]?.department || 'Select',
      //   }));
      // }
    } catch (error) {
      console.error('Error loading filters:', error);
      showError('Failed to load filters');
    } finally {
      setIsLoadingFilters(false);
    }
  };

  const handleViewReport = (studentId) => {
    const student = filteredStudents.find((s) => s.studentId === studentId);
    if (student) {
      setSelectedStudentForView(student);
      setViewModalOpen(true);
    }
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setSelectedStudentForView(null);
  };

  // Check if download buttons should be disabled
  const isDownloadDisabled = () => {
    return (
      filteredStudents?.length === 0 ||
      isPrinting ||
      activeFilters.examType === 'Select' ||
      activeFilters.department === 'Select' ||
      activeFilters.class === 'Select' ||
      (instituteType?.toLowerCase() === 'college' &&
        activeFilters.section === 'Select')
    );
  };

  // Helper function to build filter parameters
  const getFilterParams = (studentId = null) => ({
    instituteType,
    examType: activeFilters.examType,
    departmentName: activeFilters.department,
    classesName: activeFilters.class,
    sectionName: activeFilters.section,
    startDate: localStorage.getItem('startDate'),
    endDate: localStorage.getItem('endDate'),
    ...(studentId && { studentId }),
  });

  // Helper function to download file
  const downloadFile = async (blob, filename) => {
    const url = window.URL.createObjectURL(new Blob([blob]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // Generic download handler
  const handleDownload = async (downloadAction, filename) => {
    if (isPrinting) return;

    setIsPrinting(true);
    try {
      const blob = await dispatch(downloadAction).unwrap();
      await downloadFile(blob, filename);
    } catch (error) {
      console.log(error);
      error?.status == 409
        ? showError('Please enter marks for all students to download.')
        : showError('Failed to download file');
      console.error('Error downloading file:', error);
    } finally {
      setIsPrinting(false);
    }
  };

  const printMarksheet = (student) =>
    handleDownload(
      downloadStudentMarksReport(getFilterParams(student?.studentId)),
      `marksheet_${student?.studentId}.pdf`,
    );

  const printAllStudentsMarksheetsExcel = () => {
    handleDownload(
      downloadAllStudentsMarksExcel(getFilterParams()),
      `${getFilterParams().examType}_students_marksheets.xlsx`,
    );
  };

  const printAllStudentsMarksheetsPDF = () =>
    handleDownload(
      downloadAllStudentsMarksPDF(getFilterParams()),
      `${getFilterParams().examType}_students_marksheets.pdf`,
    );

  const printAllStudentsRankWisePDF = () =>
    handleDownload(
      downloadAllStudentsRankWisePDF(getFilterParams()),
      `${getFilterParams().examType}_students_rankwise.pdf`,
    );

  // Calculate subject statistics
  const calculateSubjectStats = (marks) => {
    if (!marks || marks?.length === 0) return { total: 0, obtained: 0 };

    const totalMarks = marks.reduce((sum, subject) => {
      return sum + (parseFloat(subject?.maxMarks) || 0);
    }, 0);

    const obtainedMarks = marks.reduce((sum, subject) => {
      if (subject?.grade?.toLowerCase() === 'ab') return sum;
      return sum + (parseFloat(subject?.marks) || 0);
    }, 0);

    return { total: totalMarks, obtained: obtainedMarks };
  };

  // View Marks Modal Component
  const ViewMarksModal = () => {
    if (!selectedStudentForView) return null;
    const stats = calculateSubjectStats(selectedStudentForView.marks);
    const percentage = selectedStudentForView.percentage || 0;
    const grade = selectedStudentForView.grade ?? '--';
    const overallResultColour =
      selectedStudentForView?.status?.toLowerCase() === 'fail'
        ? '#f44336'
        : selectedStudentForView?.status?.toLowerCase() === 'pass'
          ? '#4caf50'
          : '';

    return (
      <ModalDialog
        size='md'
        isOpen={viewModalOpen}
        onClose={handleCloseViewModal}
        title={
          <Box className='flex items-center gap-3'>
            <Avatar className='size-12 bg-purple-600 text-white font-bold text-lg'>
              {selectedStudentForView?.studentName?.charAt(0)}
            </Avatar>
            <Box className='flex flex-row items-center'>
              <Typography variant='h6' className='font-semibold'>
                {selectedStudentForView?.studentName} (
                {selectedStudentForView?.studentId})
              </Typography>
              <Typography variant='body2' className='text-gray-500'>
                • Marks Report
              </Typography>
            </Box>
          </Box>
        }
        footer={
          <Box className='flex gap-2 w-full justify-end'>
            <Button
              variant='outlined'
              onClick={handleCloseViewModal}
              color='inherit'
            >
              Close
            </Button>
            <Button
              variant='contained'
              startIcon={<Print />}
              onClick={() => {
                printMarksheet(selectedStudentForView);
              }}
              disabled={isPrinting}
              className='bg-purple-600'
            >
              {isPrinting ? 'Downloading...' : 'Download PDF'}
            </Button>
          </Box>
        }
      >
        <Box className='space-y-4'>
          {/* Student Info Card */}
          <Paper className='p-4 bg-gradient-to-r from-purple-50 to-blue-50'>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant='caption' className='text-gray-600'>
                  Examination
                </Typography>
                <Typography variant='body1' className='font-semibold'>
                  {activeFilters?.examType || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant='caption' className='text-gray-600'>
                  Department
                </Typography>
                <Typography variant='body1' className='font-semibold'>
                  {activeFilters?.department || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant='caption' className='text-gray-600'>
                  Class
                </Typography>
                <Typography variant='body1' className='font-semibold'>
                  {activeFilters?.class || 'N/A'}
                </Typography>
              </Grid>
              {instituteType?.toLowerCase() === 'college' && (
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant='caption' className='text-gray-600'>
                    Section
                  </Typography>
                  <Typography variant='body1' className='font-semibold'>
                    {activeFilters?.section || 'N/A'}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>

          {/* Performance Summary */}
          <Box className='grid grid-cols-2 md:grid-cols-4 gap-3'>
            <Paper className='p-3 text-center border-l-4 border-blue-500'>
              <Typography variant='caption' className='text-gray-600'>
                Total Marks
              </Typography>
              <Typography variant='h6' className='font-bold text-blue-600'>
                {stats.total}
              </Typography>
            </Paper>
            <Paper className='p-3 text-center border-l-4 border-green-500'>
              <Typography variant='caption' className='text-gray-600'>
                Obtained Marks
              </Typography>
              <Typography variant='h6' className='font-bold text-green-600'>
                {stats.obtained.toFixed(1)}
              </Typography>
            </Paper>
            <Paper className='p-3 text-center border-l-4 border-purple-500'>
              <Typography variant='caption' className='text-gray-600'>
                Percentage
              </Typography>
              <Typography variant='h6' className='font-bold text-purple-600'>
                {percentage}%
              </Typography>
            </Paper>
            <Paper className='p-3 text-center border-l-4 border-orange-500'>
              <Typography variant='caption' className='text-gray-600'>
                Grade
              </Typography>
              <Typography variant='h6' className='font-bold text-orange-600'>
                {grade}
              </Typography>
            </Paper>
          </Box>

          <Divider />

          {/* Marks Table */}
          <Box>
            <Typography
              variant='h6'
              className='mb-3 font-semibold text-gray-700'
            >
              Subject-wise Marks Details
            </Typography>
            <TableContainer
              component={Paper}
              variant='outlined'
              sx={{ maxHeight: '50vh', overflow: 'auto' }}
            >
              <Table stickyHeader size='small'>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                    <TableCell>
                      <strong>S.No</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Subject</strong>
                    </TableCell>
                    <TableCell align='center'>
                      <strong>Max Marks</strong>
                    </TableCell>
                    <TableCell align='center'>
                      <strong>Pass Marks</strong>
                    </TableCell>
                    <TableCell align='center'>
                      <strong>Obtained Marks</strong>
                    </TableCell>
                    <TableCell align='center'>
                      <strong>Grade</strong>
                    </TableCell>
                    <TableCell align='center'>
                      <strong>Status</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedStudentForView?.marks?.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={'7'} align='center'>
                        Please Enter Marks for the Student
                      </TableCell>
                    </TableRow>
                  )}
                  {selectedStudentForView?.marks?.map((subject, index) => {
                    const marks = subject?.marks ?? '--';
                    const maxMarks = subject?.maxMarks || 100;
                    const passMarks = subject?.passMark || 35;
                    const grade = subject?.grade ?? '--';
                    const status = subject?.status;

                    return (
                      <TableRow
                        key={index}
                        hover
                        // sx={{
                        //   backgroundColor: isAbsent
                        //     ? '#fff3cd'
                        //     : !isPassed
                        //       ? '#f8d7da'
                        //       : 'inherit',
                        // }}
                      >
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <Typography variant='body2' className='font-medium'>
                            {subject?.subject || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell align='center'>
                          <Typography variant='body2'>{maxMarks}</Typography>
                        </TableCell>
                        <TableCell align='center'>
                          <Typography variant='body2'>{passMarks}</Typography>
                        </TableCell>
                        <TableCell align='center'>
                          <Typography variant='body2' className='font-semibold'>
                            {marks}
                          </Typography>
                        </TableCell>
                        <TableCell align='center'>
                          <Typography variant='body2' className='font-semibold'>
                            {grade}
                          </Typography>
                        </TableCell>
                        <TableCell align='center'>
                          {status?.toLowerCase() === 'absent' ? (
                            <Chip
                              icon={<Cancel />}
                              label='Absent'
                              size='small'
                              color='warning'
                              variant='outlined'
                            />
                          ) : status?.toLowerCase() === 'pass' ? (
                            <Chip
                              icon={<CheckCircle />}
                              label='Pass'
                              size='small'
                              color='success'
                              variant='outlined'
                            />
                          ) : status?.toLowerCase() === 'fail' ? (
                            <Chip
                              icon={<Cancel />}
                              label='Fail'
                              size='small'
                              color='error'
                              variant='outlined'
                            />
                          ) : (
                            <b>--</b>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Overall Result */}
          <Paper
            className='p-4 text-center'
            sx={{
              background: `linear-gradient(135deg, ${overallResultColour}15 0%, ${overallResultColour}30 100%)`,
              border: `2px solid ${overallResultColour}`,
            }}
          >
            <Typography variant='h5' className='font-bold mb-1'>
              Overall Result
            </Typography>
            <Chip
              label={
                selectedStudentForView?.status?.toLowerCase() === 'pass'
                  ? 'PASS'
                  : selectedStudentForView?.status?.toLowerCase() === 'fail'
                    ? 'FAIL'
                    : '--'
              }
              size='large'
              style={{
                backgroundColor: overallResultColour,
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1.1rem',
                padding: '20px 30px',
              }}
            />
          </Paper>
        </Box>
      </ModalDialog>
    );
  };

  return (
    <>
      <div className='space-y-6'>
        {/* Search & Filter Section */}
        {isLoadingFilters ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              padding: '2rem',
            }}
          >
            <Spinner size='large' />
          </div>
        ) : (
          <FilterStudents
            departments={departments}
            examTypes={examTypes}
            initialFilters={currentFilters}
            onFiltersChange={handleFiltersChange}
            onSearch={handleSearch}
            onClearFilters={handleClearFilters}
          />
        )}

        {/* Available Student Reports */}
        <Card>
          <CardContent>
            <Box className='flex items-start justify-between  '>
              <Typography
                variant='h6'
                className='mb-4 text-blue-700 font-semibold'
              >
                Available Student Reports
              </Typography>
              <Box className='flex justify-end flex-wrap'>
                <IconButton
                  onClick={printAllStudentsRankWisePDF}
                  disabled={isDownloadDisabled()}
                  className={`hover:bg-green-100 mr-2 ${
                    isDownloadDisabled() ? 'text-gray-400' : 'text-green-600'
                  }`}
                  title='Download All Students Rank Wise (PDF)'
                >
                  <LuListOrdered />
                </IconButton>
                <IconButton
                  onClick={printAllStudentsMarksheetsPDF}
                  disabled={isDownloadDisabled()}
                  className={`hover:bg-red-100 mr-2 ${
                    isDownloadDisabled() ? 'text-gray-400' : 'text-red-600'
                  }`}
                  title='Download All Reports (PDF)'
                >
                  <BsFileEarmarkPdf />
                </IconButton>
                <IconButton
                  onClick={printAllStudentsMarksheetsExcel}
                  disabled={isDownloadDisabled()}
                  className={`hover:bg-sky-100 mr-2 ${
                    isDownloadDisabled() ? 'text-gray-400' : 'text-sky-600'
                  }`}
                  title='Download All Reports (Excel)'
                >
                  <RiFileExcel2Line />
                </IconButton>
              </Box>
            </Box>
            <Box className='flex mb-4 space-y-2 '>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    variant='outlined'
                    size='small'
                    placeholder='Search by student name or ID'
                    fullWidth
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size='small' required>
                    <InputLabel>Grade</InputLabel>
                    <Select
                      MenuProps={MenuProps}
                      sx={{ minWidth: 80 }}
                      value={selectedGrade || 'All'}
                      label='Grade'
                      onChange={(e) => setSelectedGrade(e.target.value)}
                    >
                      <MenuItem value='All'>All</MenuItem>
                      {grades?.map((grade) => (
                        <MenuItem key={grade?.id} value={grade?.grade}>
                          {grade?.grade}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
            <Box className='space-y-3'>
              {students?.studentMaks && (
                <Box>
                  <Typography>Exam Type : {students.examType}</Typography>
                  <Typography>
                    Total Marks : {students?.studentMaks?.[0]?.totalMarks ?? 5}
                  </Typography>
                </Box>
              )}
              <TableContainer
                component={Paper}
                variant='outlined'
                sx={{ maxHeight: '50vh', overflow: 'auto' }}
              >
                <Table stickyHeader size='small'>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                      <TableCell>
                        <strong>Student ID</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Name</strong>
                      </TableCell>
                      <TableCell align='center'>
                        <strong>Roll No</strong>
                      </TableCell>
                      <TableCell align='center'>
                        <strong>Marks Obtained</strong>
                      </TableCell>
                      <TableCell align='center'>
                        <strong>Percentage</strong>
                      </TableCell>

                      <TableCell align='center'>
                        <strong>Grade</strong>
                      </TableCell>
                      <TableCell align='center'>
                        <strong>Status</strong>
                      </TableCell>
                      <TableCell align='center'>
                        <strong>Actions</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredStudents?.map((report) => (
                      <TableRow
                        onClick={() => handleViewReport(report.studentId)}
                        key={report.id}
                        hover
                        sx={{
                          '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                        }}
                      >
                        <TableCell>{report.studentId}</TableCell>
                        <TableCell>
                          <Typography variant='body2' className='font-medium'>
                            {report?.studentName || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell align='center'>
                          <Typography variant='body2'>
                            {report?.rollNumber}
                          </Typography>
                        </TableCell>
                        <TableCell align='center'>
                          <Typography variant='body2'>
                            {report?.totalObtainedMarks}
                          </Typography>
                        </TableCell>
                        <TableCell align='center'>
                          <Typography variant='body2' className='font-semibold'>
                            {report?.percentage}%
                          </Typography>
                        </TableCell>
                        <TableCell align='center' className='font-semibold'>
                          {report?.grade ?? '--'}
                        </TableCell>
                        <TableCell align='center'>
                          <Typography variant='body2' className='font-bold'>
                            {report?.status?.toLowerCase() === 'pass' ? (
                              <div className='text-green-600 '>Pass</div>
                            ) : report?.status?.toLowerCase() === 'fail' ? (
                              <div className='text-red-600 '>Fail</div>
                            ) : report?.status?.toLowerCase() === 'absent' ? (
                              <div className='text-blue-500 '>Absent</div>
                            ) : (
                              (report?.status ?? '--')
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell className='flex items-center justify-center'>
                          <IconButton
                            sx={{ marging: 0 }}
                            onClick={() => handleViewReport(report.studentId)}
                            className='hover:bg-purple-100 text-gray-400 !cursor-pointer m-0'
                          >
                            <VisibilityOutlinedIcon />
                          </IconButton>
                          <IconButton
                            sx={{ marging: 0 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              printMarksheet(report);
                            }}
                            disabled={isPrinting}
                            className='hover:bg-purple-100 text-blue-500 !cursor-pointer m-0'
                          >
                            <LiaFileDownloadSolid />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {filteredStudents?.length === 0 && (
                <Box className='text-center py-8'>
                  <Typography variant='body1' className='text-gray-500'>
                    No reports found matching your criteria
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Footer Info */}
            <Box className='mt-4 pt-4 border-t border-gray-200 '>
              <Typography
                variant='body2'
                className='text-center text-purple-600 '
              >
                📋 Click on any student card above or use the filters to search
                for specific reports
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* View Marks Modal */}
        <ViewMarksModal />
      </div>
      <ToastNotification
        open={notification.open}
        onClose={hideNotification}
        message={notification.message}
        title={notification.title}
        severity={notification.severity}
        autoHideDuration={notification.autoHideDuration}
        position={notification.position}
      />
    </>
  );
};

export default MarksReportTab;
